// KYRS screen help (the "?" on KYRS). Plain words; keep it in step with the screen
// (apps/mobile/src/tools/KyrsTool.tsx): list names, column labels, menu items.
import type { ScreenHelpContent } from '../../v10/ScreenHelp';

export const kyrsHelp: ScreenHelpContent = {
  title: 'KYRS',
  intro:
    'KYRS is where all your tasks live. Tasks sit in lists, and each task gets a priority score so you can see what to do first. The Aims you save in ATLAS turn into tasks here.',
  sections: [
    {
      heading: 'My Lists',
      body: [
        'The first screen shows your lists. The number next to a name is how many open tasks it has. Tap a list to open it.',
      ],
      items: [
        { term: 'All Active Tasks', text: 'every open task from all your lists in one place. You can look and tick things off, but you add tasks inside a real list.' },
        { term: 'SHAPE', text: 'tap it to open your five life-area lists (Social, Health, Admin, Personal, Economic). ATLAS Aims land in these.' },
        { term: 'Completed', text: 'tasks you have ticked off, newest first.' },
        { term: 'Auto-Scheduled', text: 'a green tag on a list that has the Auto-Scheduler switched on.' },
      ],
    },
    {
      heading: 'The buttons up top',
      items: [
        { term: 'PAGE / DOWN / OUT', text: 'three ways to look. PAGE opens a list on its own screen. DOWN opens its tasks right under its name. OUT puts your lists in a strip with the tasks below.' },
        { term: 'Bell', text: 'the red number is how many auto-scheduled tasks are waiting for you to check (see Auto-Scheduler below).' },
        { term: 'Add List', text: 'type a name and it makes a new list with three placeholder tasks to rename.' },
        { term: '⋮ (next to a list)', text: 'opens that list\'s menu: Add Category, Tabled On/Off, Rename, Include or Exclude from All Active Tasks, Auto-Scheduler, Delete List. Delete List removes the list and all its tasks, and it cannot be undone. In OUT, press and hold a list to get the same menu.' },
        { term: '‹ OUT', text: 'inside a list, takes you back to My Lists. The 🗑️ and 🔔 icons sit at the top right.' },
      ],
    },
    {
      heading: 'Inside a list',
      body: [
        'Tasks are shown in a table. The top block is the list itself; below it come your categories (sections you make with Add Category). Tasks with no category are in the top block.',
        'To add a task, type in the "Type new task title..." line at the bottom of a block and press return. Add Task opens the full form instead. Long View / Short View switches how much of the table you see.',
      ],
      items: [
        { term: '☰', text: 'move a task up or down. Works when the table is sorted by its own order, not in All Active Tasks or Completed.' },
        { term: 'Done', text: 'tap the box to tick a task off. Done tasks leave the top block and show up in Completed.' },
        { term: 'D / Date', text: 'when it is scheduled. Tap it to pick Today, Tomorrow, Custom… or Clear.' },
        { term: 'Title', text: 'tap to open the task and edit it. Drag the small handle on the right of the heading to make the column wider.' },
        { term: 'Shape', text: 'Long View only. Which life area the task belongs to. Tap to change it. The choices are your areas from ATLAS.' },
        { term: 'Urg, Imp, Eff…', text: 'Long View only. One column for each rating you use (the first three letters of its name). Tap to pick a rating.' },
        { term: 'ETC', text: 'Long View only. How long it will take. Tap to pick.' },
        { term: 'Pri', text: 'Long View only. The priority score. Tap the number to see how it was added up.' },
        { term: 'Add', text: 'Long View only. Type a number to give a task extra points by hand. It saves when you leave the box.' },
        { term: '•••', text: 'Edit, Subtask…, Move to list…, Category…, Unlist, Delete.' },
      ],
    },
    {
      heading: 'Priority score',
      body: [
        'The score adds points from the task\'s Group and Subgroups, points from each rating you chose (like Urgency or Importance), a bonus if the task has no date, and whatever you typed in Add. A bigger number means do it sooner.',
        'The pill is green for 4 or less, amber for 5 to 7 and red above 7.',
        'To see your best moves first, tap the Pri heading. Tap Date, Title or ETC to sort by those instead. Tap again to flip the order. The arrow shows which way it is going.',
      ],
    },
    {
      heading: 'Editing a task',
      body: [
        'Tap a title (or ••• then Edit) to open the form: Title, Completed, Category / section, Group, Subgroup 1 and 2, your ratings, ETC (duration), Scheduled (yyyy-MM-dd HH:mm, or Today, Tomorrow, Clear), Priority add, Notes, Unlisted, Parent task and Move to list. Title is the only thing you must fill in. Press Save at the bottom to keep your changes, or Cancel.',
        'Quick changes made right in the table (ticking Done, the Date, Shape, rating, ETC and Add cells, Category…, Unlist) save the moment you make them.',
        'Subtask… lets you add a subtask under a task, or make one task a subtask of another. Subtasks sit indented under their parent. Tap the little arrow to open or close them, or use Expand All / Collapse All at the top.',
        'Unlisted tasks have no tick box and do not count or get scheduled.',
        'Your Groups, ratings and ETC choices come from your task settings. KYRS only reads them.',
      ],
    },
    {
      heading: 'Categories',
      body: [
        'Tap a category heading to Rename Category, choose Show completed here or Hide completed here (shown ones stay on the list crossed out), or Delete Category (its tasks go back to the top block). The Tabled section stays hidden until you turn "Tabled: On" in the list\'s ⋮ menu.',
      ],
    },
    {
      heading: 'Auto-Scheduler',
      body: [
        'In a list\'s ⋮ menu, open Auto-Scheduler to set up a list: the on/off switch, a Default Group, a Default ETC and an OLMPS Sector (every task in the list then counts toward that area on ASTRA). The switch only works once the Default Group has scheduled time slots and a Default ETC is picked. Press Save to keep it.',
        'With it on, the list gets dates for you whenever you add a task, tick one, or delete one. "Reschedule this list now" clears and redoes every eligible task. Turning it off clears the dates it set.',
        'Each date it sets waits for you in the bell. Open it to see them as a List or a Calendar. Keep accepts one, Keep All accepts the lot, and Reschedule lets you type a different date (yyyy-MM-dd) and time (HH:mm).',
      ],
    },
    {
      heading: 'Deleting and the trash',
      body: [
        'Deleting a task asks you first. Afterwards a bar says Deleted with an UNDO button for five seconds. After that, open the 🗑️ in a list to Restore a task, Delete Forever, or Empty Trash. Delete Forever and Empty Trash cannot be undone.',
      ],
    },
  ],
};
